import React, { FunctionComponent, useMemo, useRef } from "react"
import {
  ProbabilityDensityChartOptions,
  ProbabilityDensityContainer,
} from "@features/results/Result/ChartsContainer/ProbabilityDensityContainer"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { DashboardChartContainer } from "@features/results/Result/Dashboard/DashboardChartContainer"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import { RunDto } from "@custom-types/modelRun"
import { GraphNodeType } from "@custom-types/graphs"
import { useGetRunsNodesResultsHistoricQuery } from "@store/services/runService"
import {
  ChartGroupLayout,
  DashboardProbabilityDensityChartDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { KernelEstimationFunction } from "@features/results/Result/const"
import { SxProps } from "@mui/material"
import { calculateContainerLayoutDashboardHeight } from "@features/results/Result/Dashboard/layout"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"
import { calculateLagShift } from "@utils/run"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"

// normally it should work all cases with height of "100%", but once chart container's height is limited
// by responsive value then chart starts to expand
// https://www.chartjs.org/docs/2.9.4/general/responsive.html
const getContainerSx = (
  chart: DashboardProbabilityDensityChartDto,
  layout?: ChartGroupLayout,
): SxProps => {
  const titleHeight = 42 // TODO kk: handle multi-rows title
  const sliderHeight = 106

  if (!layout)
    return { height: `calc(100% - ${titleHeight}px - ${sliderHeight}px)`, minHeight: 275 }

  const height = calculateContainerLayoutDashboardHeight(chart, layout)
  if (!height) return {}

  return { height: `calc(${height}vh - ${titleHeight}px - ${sliderHeight}px)` }
}

const chartOptions: ProbabilityDensityChartOptions = {
  isHistogramShown: true,
  isEstimationShown: false,
  isIQRangeVisible: false,
  isViolinDiagram: false,
  kernel: [KernelEstimationFunction.Epanechnikov],
  year: 0,
  secondYear: 0,
  sliderThresholdValue: 60,
  sliderEstimationBandwidthValue: 1,
  isSecondYear: false,
  isDemean: false,
  minVal: 0,
  maxVal: 0,
  clamp: false,
  clamp_stdev: 1,
  clamp_string: "Show area % within " + 1 + " st.dev",
  isCDF: false,
  showSuprenums: false,
}

interface Props {
  chart: DashboardProbabilityDensityChartDto
  runs: RunDto[]
  layout?: ChartGroupLayout
}

export const DashboardProbabilityDensityChart: FunctionComponent<Props> = (props) => {
  const [cfg, updateCfg] = useDashboardConfig({
    showLegend: true,
    selectedNodes: props.chart.Nodes || props.chart.NodeSelect?.[0]?.Nodes || [],
    hiddenDatasets: [],
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(containerRef)

  const selectedNodes = cfg.selectedNodes ?? []

  const handleShowLegend = () => {
    updateCfg((draft) => {
      draft.showLegend = !draft.showLegend
    })
  }

  const handleOptionChange = (nodes: string[]) => {
    updateCfg((draft) => {
      draft.selectedNodes = nodes
    })
  }

  const handleLegendClick = (datasetLabel: string) => {
    updateCfg((draft) => {
      const hiddenDatasets = draft.hiddenDatasets ?? []
      if (hiddenDatasets.includes(datasetLabel)) {
        draft.hiddenDatasets = hiddenDatasets.filter((label) => label !== datasetLabel)
      } else {
        draft.hiddenDatasets = [...hiddenDatasets, datasetLabel]
      }
    })
  }

  const handleFullscreen = () => {
    updateCfg((draft) => {
      draft.isFullscreen = !draft.isFullscreen
    })
  }

  const runIds = props.runs.map((run) => run.key)
  const { isFetching, data: historicResponse } = useGetRunsNodesResultsHistoricQuery(
    {
      runKeys: runIds,
      nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
    },
    {
      skip: selectedNodes.length === 0 || !isVisible,
    },
  )

  const runResults = useMemo(
    () => convertHistoricToRunResults(historicResponse?.results),
    [historicResponse?.results],
  )
  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )

  const key = graphViewGroupNodeLabelToSampleNode(selectedNodes[0])
  const nodeLabel: GroupNodeLabel = {
    key,
    label: selectedNodes[0] ?? "",
    type: GraphNodeType.DETERMINISTIC,
    group: "",
    sort: "",
  }

  return (
    <DashboardChartContainer
      chart={props.chart}
      layout={props.layout}
      onFullScreen={handleFullscreen}
      onOptionChange={(v) => handleOptionChange(v.Nodes)}
      runIds={runIds}
      onShowLegendToggle={handleShowLegend}
      showLegend={cfg.showLegend}
      ref={containerRef}
    >
      {isVisible && (
        <ProbabilityDensityContainer
          runs={props.runs}
          isFetchingGetRunFilterNode={isFetching}
          rawResultDataFilterNodes={runResults}
          lagShift={lagShift}
          selectedNode={nodeLabel}
          localStoragePrefix={"dashboard_comparison"}
          onlyChart
          showYearSlider
          chartOptions={chartOptions}
          maintainAspectRatio={false}
          chartContainerSx={{
            height: "100%",
            position: "relative",
          }}
          sx={
            cfg.isFullscreen
              ? { height: window.innerHeight - 175, width: "100%" }
              : getContainerSx(props.chart, props.layout)
          }
          legendPosition={props.chart.LegendPosition}
          showLegend={cfg.showLegend}
          hiddenDatasets={cfg.hiddenDatasets}
          onLegendClick={handleLegendClick}
        />
      )}
    </DashboardChartContainer>
  )
}
